import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ArrowLeft, FileText, Wrench, CalendarDays } from "lucide-react";

export default function PlotDetail() {
  const { id } = useParams();
  const [plot, setPlot] = useState(null);
  const [activities, setActivities] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tasks, setTasks] = useState([]);

  const load = useCallback(async () => {
    const [p, a, d, m] = await Promise.all([
      api.get(`/plots/${id}`),
      api.get(`/activities?plot_id=${id}`),
      api.get(`/documents?plot_id=${id}`),
      api.get(`/maintenance?plot_id=${id}`),
    ]);
    setPlot(p.data); setActivities(a.data); setDocs(d.data); setTasks(m.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!plot) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <Link to="/app/plots" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={14} /> All plots</Link>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Plot detail</div>
          <h1 className="heading text-4xl mt-1">Plot {plot.plot_number}</h1>
          <div className="text-sm text-muted-foreground mt-1">{plot.size_sqft ? `${Math.round(plot.size_sqft).toLocaleString("en-IN")} sq ft` : "—"} · Status {plot.status}</div>
        </div>
        {plot.has_cottage && (
          <Link to="/app/bookings"><Button data-testid="book-cottage-btn"><CalendarDays size={14} className="mr-2" /> Book cottage</Button></Link>
        )}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <section>
          <h2 className="heading text-xl mb-3">Activity timeline</h2>
          <div className="border border-border rounded-lg bg-card divide-y divide-border">
            {activities.length === 0 && <div className="p-4 text-sm text-muted-foreground">No activity yet.</div>}
            {activities.map((a) => (
              <div key={a.id} className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{a.activity_type}</div>
                <div className="text-sm font-medium mt-1">{a.title}</div>
                {a.description && <div className="text-xs text-muted-foreground mt-1">{a.description}</div>}
                <div className="text-[10px] text-muted-foreground mt-2">{new Date(a.performed_at).toLocaleString()} · {a.performed_by}</div>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="heading text-xl mb-3">Documents</h2>
          <div className="border border-border rounded-lg bg-card divide-y divide-border">
            {docs.length === 0 && <div className="p-4 text-sm text-muted-foreground">No documents.</div>}
            {docs.map((d) => (
              <a key={d.id} href={d.url} target="_blank" rel="noreferrer" className="p-4 flex items-center gap-3 hover:bg-secondary/40 transition-[background-color] duration-200">
                <FileText size={16} className="text-primary" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.title}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{d.doc_type}</div>
                </div>
              </a>
            ))}
          </div>

          <h2 className="heading text-xl mt-8 mb-3">Maintenance</h2>
          <div className="border border-border rounded-lg bg-card divide-y divide-border">
            {tasks.length === 0 && <div className="p-4 text-sm text-muted-foreground">No open tasks.</div>}
            {tasks.map((t) => (
              <div key={t.id} className="p-4 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2"><Wrench size={14} /> {t.title}</div>
                  {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
                </div>
                <span className={`status-pill status-${t.status}`}>{t.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
